import { Badge, Group, Stack, Text, Title } from '@mantine/core';
import { LoadingAnimation } from '@uag/react-core';
import { useTranslation } from 'react-i18next';
import { FormattedDate } from 'react-intl';

import {
    useGetClientEntraSignInSettings,
    useGetClientEntraSignInsByClientId,
} from 'api/v3/client-entra-sign-ins/client-entra-sign-ins';
import { listQueryDefault } from 'app/queryDefaults';
import { usePermissions } from 'modules/shared/permissions';
import { FieldDataType } from 'shared/components/extendedDataTable';
import { OverviewGrid } from 'shared/components/OverviewGrid';
import { useClientFormContext } from '../ClientFormProvider';
import { AddEntraSignInButton } from './AddEntraSignInButton';
import { DeleteEntraSignInAction } from './DeleteEntraSignInAction';
import { EntraSignInHowToConnect } from './EntraSignInHowToConnect';

export const EntraSignInGrid = () => {
    const form = useClientFormContext();
    const { t } = useTranslation();
    const canReadSecret = usePermissions('ReadSecret');
    const canDeleteSecret = usePermissions('DeleteSecret');
    const canCreateSecret = usePermissions('CreateSecret');
    const clientId = form.values.basicInformation.id ?? '';

    const { data: settings, isLoading: isLoadingSettings } = useGetClientEntraSignInSettings({
        query: { enabled: canReadSecret },
    });
    const isEnabled = settings?.data.enabled === true;
    const {
        data: entraSignIns,
        error,
        isLoading,
    } = useGetClientEntraSignInsByClientId(clientId, {
        query: { enabled: !!clientId && canReadSecret && isEnabled, ...listQueryDefault.query },
    });

    if (!clientId || !canReadSecret) {
        return null;
    }

    if (isLoadingSettings) {
        return <LoadingAnimation />;
    }

    // Core accepts Entra sign-ins only where client federation is turned on
    if (!isEnabled) {
        return null;
    }

    if (isLoading || !entraSignIns?.data) {
        return <LoadingAnimation />;
    }

    const trustedTenants = settings?.data.trustedTenants ?? [];
    const getTenantName = (tenantId: string) => trustedTenants.find((t) => t.tenantId === tenantId)?.name ?? tenantId;

    return (
        <Stack>
            <Title order={4}>{t('entraSignIns')}</Title>
            <Text c="dimmed" size="sm">
                {t('entraSignInsDescription')}
            </Text>
            <OverviewGrid
                additionalRowActions={canDeleteSecret ? [DeleteEntraSignInAction] : undefined}
                data={entraSignIns.data}
                error={error}
                fieldDescriptions={[
                    {
                        header: t('name'),
                        data: 'displayName',
                        render: (item) => (
                            <Stack gap={0}>
                                <Group gap="xs">
                                    <Text>{item.displayName}</Text>
                                    {item.type === 'DeveloperGroup' && (
                                        <Badge variant="light">{t('entraTypeDeveloperGroup')}</Badge>
                                    )}
                                </Group>
                                <Text c="dimmed" ff="monospace" size="xs">
                                    {item.entraId}
                                </Text>
                            </Stack>
                        ),
                    },
                    {
                        header: t('tenant'),
                        data: 'tenantId',
                        render: (item) => <Text>{getTenantName(item.tenantId)}</Text>,
                    },
                    { header: t('description'), data: 'description' },
                    { header: t('creationDate'), data: 'created', dataType: FieldDataType.DateTime },
                    { header: t('createdBy'), data: 'createdBy.displayName' },
                    {
                        header: t('lastUsed'),
                        data: 'lastUsed',
                        render: (item) =>
                            item.lastUsed ? (
                                <Text>
                                    <FormattedDate
                                        day="numeric"
                                        hour="numeric"
                                        minute="numeric"
                                        month="numeric"
                                        value={new Date(item.lastUsed)}
                                        year="numeric"
                                    />
                                </Text>
                            ) : (
                                <Text c="dimmed">{t('never')}</Text>
                            ),
                    },
                ]}
                headerActions={
                    canCreateSecret && trustedTenants.length > 0
                        ? [
                              <AddEntraSignInButton
                                  key="AddEntraSignIn"
                                  clientId={clientId}
                                  trustedTenants={trustedTenants}
                              />,
                          ]
                        : undefined
                }
            />
            {entraSignIns.data.length > 0 && (
                <EntraSignInHowToConnect
                    clientId={clientId}
                    developerScope={settings?.data.developerScope}
                    developerTenantIds={[
                        ...new Set(
                            entraSignIns.data.filter((e) => e.type === 'DeveloperGroup').map((e) => e.tenantId),
                        ),
                    ]}
                    federationScope={settings?.data.federationScope}
                    showWorkloads={entraSignIns.data.some((e) => e.type === 'Workload')}
                />
            )}
        </Stack>
    );
};
